import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Sparkles, Plus, Copy, Trash2, Edit3, LogOut, Loader2, ExternalLink, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { listTenants, createTenant, updateTenant, deleteTenant } from "../lib/adminApi";

/** AdminPanel — müşteri demolarını yönet (CRUD). */
export default function AdminPanel() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const data = await listTenants();
      setItems(data.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        sessionStorage.removeItem("plena_admin_token");
        nav("/");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!sessionStorage.getItem("plena_admin_token")) {
      nav("/");
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const logout = () => {
    sessionStorage.removeItem("plena_admin_token");
    sessionStorage.removeItem("plena_admin_email");
    nav("/");
  };

  const onCopyLink = (slug) => {
    const url = `${window.location.origin}/demo/${slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link kopyalandı", { description: url });
  };

  const onDelete = async (slug) => {
    if (!window.confirm(`'${slug}' müşterisini ve linkini kalıcı olarak silmek istiyor musunuz?`)) return;
    await deleteTenant(slug);
    toast.success(`${slug} silindi`);
    refresh();
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-sky-700 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div className="leading-tight">
              <div className="font-display text-base font-bold tracking-tight">Plena Growth</div>
              <div className="text-[0.6rem] uppercase tracking-wider text-slate-500">Admin Console</div>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>{sessionStorage.getItem("plena_admin_email")}</span>
            <button
              type="button"
              onClick={logout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border border-slate-200 hover:border-rose-200 hover:bg-rose-50 hover:text-rose-700 transition"
            >
              <LogOut className="w-3 h-3" />
              Çıkış
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-[0.7rem] uppercase tracking-wider font-semibold text-sky-700">
              Müşteri Demoları
            </div>
            <h1 className="font-display text-3xl font-bold text-slate-900 tracking-tight mt-1">
              Yayınlanan Demolar ({items.length})
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Her müşteri için ayrı bir link ve şifre oluşturun. Aynı seed data ile çalışır.
            </p>
          </div>
          <Button
            onClick={() => { setEditing(null); setShowForm(true); }}
            data-testid="new-tenant-btn"
            className="bg-sky-600 hover:bg-sky-700 text-white gap-2"
          >
            <Plus className="w-4 h-4" /> Yeni Müşteri Demosu
          </Button>
        </div>

        {loading && (
          <div className="text-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
        )}

        {!loading && items.length === 0 && (
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-16 text-center">
            <div className="text-sm text-slate-500 mb-4">Henüz müşteri demosu yok.</div>
            <Button onClick={() => setShowForm(true)} variant="outline">
              <Plus className="w-4 h-4 mr-2" /> İlk demoyu oluştur
            </Button>
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[0.65rem] uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="text-left px-5 py-3 font-semibold">Müşteri</th>
                  <th className="text-left px-5 py-3 font-semibold">Slug / Link</th>
                  <th className="text-left px-5 py-3 font-semibold">Marka</th>
                  <th className="text-left px-5 py-3 font-semibold">Oluşturulma</th>
                  <th className="text-right px-5 py-3 font-semibold">Aksiyonlar</th>
                </tr>
              </thead>
              <tbody>
                {items.map((t) => (
                  <tr key={t.slug} className="border-t border-slate-100 hover:bg-slate-50/50" data-testid={`tenant-row-${t.slug}`}>
                    <td className="px-5 py-4">
                      <div className="font-semibold text-slate-800">{t.name}</div>
                      {t.notes && <div className="text-[0.7rem] text-slate-500 truncate max-w-[260px]">{t.notes}</div>}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <code className="text-xs bg-slate-100 px-2 py-1 rounded">/demo/{t.slug}</code>
                        <a
                          href={`/demo/${t.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sky-600 hover:text-sky-800"
                          title="Yeni sekmede aç"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        <span className="w-4 h-4 rounded border border-slate-200" style={{ background: t.brand_color }} />
                        <code className="text-xs text-slate-600">{t.brand_color}</code>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-xs text-slate-500">
                      {new Date(t.created_at).toLocaleDateString("tr-TR")}
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center justify-end gap-1.5">
                        <button onClick={() => onCopyLink(t.slug)} title="Linki kopyala" className="p-1.5 rounded hover:bg-sky-100 text-sky-700">
                          <Copy className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => { setEditing(t); setShowForm(true); }} title="Düzenle" className="p-1.5 rounded hover:bg-slate-100 text-slate-700">
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => onDelete(t.slug)} title="Sil" className="p-1.5 rounded hover:bg-rose-50 text-rose-700">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {showForm && (
        <TenantFormModal
          tenant={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); refresh(); }}
        />
      )}
    </div>
  );
}

function TenantFormModal({ tenant, onClose, onSaved }) {
  const [form, setForm] = useState({
    slug: tenant?.slug || "",
    name: tenant?.name || "",
    password: "",
    logo_url: tenant?.logo_url || "",
    brand_color: tenant?.brand_color || "#0EA5E9",
    notes: tenant?.notes || "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const isEdit = !!tenant;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      if (isEdit) {
        const payload = { name: form.name, logo_url: form.logo_url, brand_color: form.brand_color, notes: form.notes };
        if (form.password) payload.password = form.password;
        await updateTenant(tenant.slug, payload);
        toast.success("Müşteri güncellendi");
      } else {
        await createTenant(form);
        toast.success("Müşteri oluşturuldu", { description: `Link: /demo/${form.slug}` });
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Kayıt başarısız");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-display text-lg font-semibold">
            {isEdit ? "Müşteri Demosunu Düzenle" : "Yeni Müşteri Demosu"}
          </h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-700"><X className="w-5 h-5" /></button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Müşteri Adı</Label>
            <Input data-testid="tenant-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Örn: Yapı Kredi" required />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Slug (URL fragmanı)</Label>
            <Input data-testid="tenant-slug-input" value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") })} placeholder="yapikredi" disabled={isEdit} required pattern="^[a-z0-9-]{2,40}$" />
            <p className="text-[0.65rem] text-slate-500">Link: <code>{window.location.origin}/demo/{form.slug || "..."}</code></p>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Şifre {isEdit && <span className="text-slate-400 font-normal">(boş bırak = değiştirme)</span>}</Label>
            <Input data-testid="tenant-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Müşteriye verilecek şifre" required={!isEdit} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Marka Rengi</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10 w-12 border border-slate-200 rounded cursor-pointer" />
                <Input value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold">Logo URL (opsiyonel)</Label>
              <Input value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} placeholder="https://..." />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold">Notlar (opsiyonel)</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Bu demo için iç notlar…" rows={2} />
          </div>
          {err && <div className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>İptal</Button>
          <Button type="submit" data-testid="tenant-save-btn" disabled={saving} className="bg-sky-600 hover:bg-sky-700 text-white">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (isEdit ? "Güncelle" : "Oluştur")}
          </Button>
        </div>
      </form>
    </div>
  );
}
